import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import useTranslations from "@/i18n/useTranslations";

export interface LanguageOverviewProps {
  prerequisitesHtml?: string;
  syllabusHtml?: string;
  formattedLanguage?: string | null;
  formattedTranscripts?: string[];
  formattedAiLanguages?: string[];
  learningItems?: {
    sortValue: number | null;
    value: string;
  }[];
  oneColumn?: boolean;
  isCurriculumInMainContent?: boolean;
}

export default function LanguageOverview({
  prerequisitesHtml = "",
  syllabusHtml = "",
  formattedLanguage,
  formattedTranscripts = [],
  formattedAiLanguages = [],
  learningItems,
  oneColumn = false,
  isCurriculumInMainContent = false,
}: LanguageOverviewProps) {
  const t = useTranslations("Course");
  const h3ClassName = "mt-0 mb-4 text-xl md:text-2xl";
  const containerClassName = oneColumn
    ? "flex flex-col gap-6"
    : "flex flex-col sm:flex-row gap-6 md:gap-14";
  const itemClassName = oneColumn ? "" : "flex-1";

  // When the Curriculum block is in the page column, it already covers language,
  // transcripts, and prerequisites (including prerequisites-only courses). Skip duplicates.
  // When learning items are provided, another surface renders them; skip for the same reason.
  if (
    (isCurriculumInMainContent &&
      (Boolean(syllabusHtml) || prerequisitesHtml.length > 0)) ||
    (learningItems && learningItems.length > 0)
  ) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Language and Translations row */}
      <div className={containerClassName}>
        {formattedLanguage && (
          <div className={itemClassName}>
            <h3 className={h3ClassName}>{t("language")}</h3>
            <p className="text-gray-800 mb-0">{formattedLanguage}</p>
          </div>
        )}

        {formattedAiLanguages?.length > 0 && (
          <div className={itemClassName}>
            <h3 className={h3ClassName}>{t("translations")}</h3>
            <div className="flex flex-row flex-wrap gap-2">
              {formattedAiLanguages.map((language, index) => (
                <p key={index} className="text-gray-800 mb-0 pr-2">
                  {language}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Transcripts and Prerequisites row */}
      <div className={containerClassName}>
        {formattedTranscripts?.length > 0 && (
          <div className={itemClassName}>
            <h3 className={h3ClassName}>{t("transcripts")}</h3>
            <div className="flex flex-row flex-wrap gap-2">
              {formattedTranscripts.map((transcript, index) => (
                <p key={index} className="text-gray-800 mb-0 pr-2">
                  {transcript}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className={itemClassName}>
          <h3 className={h3ClassName}>{t("prerequisites")}</h3>
          {prerequisitesHtml.length > 0 ? (
            <div className="text-gray-800 [&_p]:mb-0 [&_p]:text-gray-800 [&_li]:text-lg">
              {sanitizeAndParseInlineHTML(prerequisitesHtml)}
            </div>
          ) : (
            <p className="text-gray-800 mb-0">{t("none")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
